"use client";
import ReactDOM from "react-dom";
import { useEffect, useState } from "react";
import { Logger } from "@shared/utils/logger";

interface PipPortalProps {
  children: React.ReactNode;
  pipWindow: Window;
}

/**
 * PipPortal - Renders children into the Picture-in-Picture window using React Portal.
 * Also handles copying styles from the main window.
 */
export const PipPortal = ({ children, pipWindow }: PipPortalProps) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!pipWindow) return;

    // Setup document structure
    const setupPipDocument = () => {
      // Add viewport meta tag for better mobile rendering
      const viewportMeta = pipWindow.document.createElement("meta");
      viewportMeta.name = "viewport";
      viewportMeta.content = "width=device-width, initial-scale=1.0";
      pipWindow.document.head.appendChild(viewportMeta);

      // Add charset meta tag
      const charsetMeta = pipWindow.document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      pipWindow.document.head.appendChild(charsetMeta);

      // Set document title
      pipWindow.document.title = "Meeting - Picture in Picture";

      // Set body styles
      pipWindow.document.body.style.margin = "0";
      pipWindow.document.body.style.padding = "0";
      pipWindow.document.body.style.overflow = "hidden";
      pipWindow.document.body.style.backgroundColor = "#111111";
    };

    setupPipDocument();

    // Create a container div in the PiP window
    const div = pipWindow.document.createElement("div");
    div.id = "pip-root";
    div.style.height = "100%";
    div.style.width = "100%";
    pipWindow.document.body.appendChild(div);
    setContainer(div);

    // Copy styles from main window to PiP window
    const copyStyles = () => {
      const styles = Array.from(document.styleSheets);
      let copiedCount = 0;
      let failedCount = 0;

      styles.forEach((styleSheet) => {
        try {
          if (styleSheet.href) {
            // Link external stylesheets (Tailwind, etc.)
            const newLink = pipWindow.document.createElement("link");
            newLink.rel = "stylesheet";
            newLink.href = styleSheet.href;
            newLink.type = "text/css";
            pipWindow.document.head.appendChild(newLink);
            copiedCount++;
          } else if (styleSheet.cssRules) {
            // Copy inline styles
            const newStyle = pipWindow.document.createElement("style");
            try {
              Array.from(styleSheet.cssRules).forEach((rule) => {
                newStyle.appendChild(pipWindow.document.createTextNode(rule.cssText));
              });
              pipWindow.document.head.appendChild(newStyle);
              copiedCount++;
            } catch (innerError) {
              // CORS or access issues with specific rules
              Logger.warn("PipPortal", "Failed to copy individual CSS rules", innerError);
              failedCount++;
            }
          }
        } catch (e) {
          Logger.warn("PipPortal", "Failed to copy stylesheet", e);
          failedCount++;
        }
      });

      Logger.info("PipPortal", `Style copying complete: ${copiedCount} succeeded, ${failedCount} failed`);
    };

    copyStyles();

    // Copy font faces to PiP window for consistent typography
    const copyFonts = () => {
      try {
        if (document.fonts && pipWindow.document.fonts) {
          document.fonts.forEach((font) => {
            if (font.status === "loaded") {
              pipWindow.document.fonts.add(font);
            }
          });
        }
      } catch (e) {
        Logger.warn("PipPortal", "Failed to copy fonts", e);
      }
    };

    copyFonts();

    // Cleanup
    return () => {
      if (div && pipWindow.document.body.contains(div)) {
        pipWindow.document.body.removeChild(div);
      }
    };
  }, [pipWindow]);

  if (!container) return null;

  return ReactDOM.createPortal(children, container);
};
